/**
 * modelStatusArming.ts — the self-arming condition for the model-status poll (content/state split
 * plan, thinking/plans/2026_08_11_content_state_split.md, Phase 1 step 2). Today's nine scattered
 * boolean conditions across UserBlock/TrainingBand/YourModelBlock/pack pages all reduce to ONE
 * rule: "the model row is in a transitional state" — training, a retrain in flight over an
 * already-served checkpoint, or ready-but-not-yet-seen. Pure — no I/O, no React — so the condition
 * itself is unit-testable independent of the fetch/interval plumbing in
 * `useModelStatusProvider.tsx`, which is the one caller.
 */

export interface ArmingModel {
    status: 'training' | 'ready' | 'failed'
    retraining?: boolean
    delivered?: boolean
}

/** True while the model is worth polling for: training, retraining over a served checkpoint, or
 *  ready but not yet seen on the celebration surface. False (disarmed) once ready-and-delivered,
 *  or failed with no live retrain — the terminal, stable states nothing else can move it out of
 *  without a fresh Start. `null` (no model row, or anonymous) is disarmed — there is nothing to
 *  poll for. */
export function isModelStatusArmed(model: ArmingModel | null): boolean {
    if (!model) return false
    if (model.status === 'training' || model.retraining) return true
    return model.status === 'ready' && !model.delivered
}
